import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'pushpop', label: 'Push / Pop Sandbox' },
  { key: 'parens', label: 'Valid Parentheses' },
  { key: 'nextgreater', label: 'Next Greater Element' },
  { key: 'minstack', label: 'Min Stack' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Vertical stack view, bottom to top, top marked ────────────────── */
const StackView = ({ label, stack, highlightTop, badTop, color = V1 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 130 }}>
    <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontSize: 10, fontWeight: 800, color: color, marginBottom: 4 }}>{stack.length > 0 ? 'top' : ''}</div>
    <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: 4, minHeight: 46 }}>
      {stack.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 0' }}>empty</div>}
      {stack.map((v, i) => {
        const isTop = i === stack.length - 1;
        const bad = isTop && badTop;
        return (
          <div key={i} style={{
            width: 110, height: 40, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 14, fontFamily: "'Inter',sans-serif",
            background: bad ? `linear-gradient(135deg,${RED},#c0293a)` : (isTop && highlightTop) ? `linear-gradient(135deg,${color},${V2})` : 'var(--bg3)',
            border: `1.5px solid ${bad ? RED : (isTop && highlightTop) ? color : 'var(--border)'}`,
            color: (bad || (isTop && highlightTop)) ? '#fff' : 'var(--text)',
            transition: 'all 0.2s ease',
          }}>{v}</div>
        );
      })}
    </div>
    <div style={{ width: 118, height: 3, marginTop: 4, borderRadius: 2, background: 'var(--border-strong)' }} />
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

/* ─── Push / Pop Sandbox ─────────────────────────────────────────── */
const PushPopMode = () => {
  const [stack, setStack] = useState([3, 7]);
  const [value, setValue] = useState('');
  const [lastAction, setLastAction] = useState({ action: null });

  const push = () => {
    if (value.trim() === '' || isNaN(Number(value))) return;
    setStack(s => [...s, Number(value)]);
    setLastAction({ action: 'push', value: Number(value) });
    setValue('');
  };
  const pop = () => {
    if (stack.length === 0) return;
    const popped = stack[stack.length - 1];
    setStack(s => s.slice(0, -1));
    setLastAction({ action: 'pop', value: popped });
  };
  const peek = () => {
    if (stack.length === 0) return;
    setLastAction({ action: 'peek', value: stack[stack.length - 1] });
  };

  const code = {
    lines: [
      'Deque<Integer> stack = new ArrayDeque<>();',
      'stack.push(value);   // add to the top',
      'int top = stack.pop();   // remove and return the top',
      'int top = stack.peek();  // look at the top, don\'t remove',
      'boolean empty = stack.isEmpty();',
    ],
    active: !lastAction.action ? -1 : lastAction.action === 'push' ? 1 : lastAction.action === 'pop' ? 2 : 3,
  };
  const note = !lastAction.action ? 'Push a value, or pop/peek the stack.' :
    lastAction.action === 'push' ? `Pushed ${lastAction.value} onto the top.` :
    lastAction.action === 'pop' ? `Popped ${lastAction.value} off the top — it's removed from the stack.` :
    `Peeked: the top is ${lastAction.value} — nothing was removed.`;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 18 }}>
          <StackView label="stack" stack={stack} highlightTop={lastAction.action === 'push' || lastAction.action === 'peek'} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={value} onChange={e => setValue(e.target.value)} style={smallInput} placeholder="value" />
            <button onClick={push} style={smallBtn(V1)}>Push</button>
            <button onClick={pop} style={smallBtn(RED, stack.length === 0)} disabled={stack.length === 0}>Pop</button>
            <button onClick={peek} style={smallBtn(TEAL, stack.length === 0)} disabled={stack.length === 0}>Peek</button>
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
          <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>Notice push and pop both only ever touch the top — that's the whole definition of LIFO (last in, first out).</p>
        </div>
      </div>
      <CodePanel title="Java — Stack via Deque" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Valid Parentheses ──────────────────────────────────────────── */
const PAIRS = { ')': '(', ']': '[', '}': '{' };
const OPENERS = new Set(['(', '[', '{']);
const ParensMode = () => {
  const [text, setText] = useState('{[()]}');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startCheck = () => {
    const s = text.trim();
    if (!s) return;
    const built = [];
    const stack = [];
    let failed = false;
    for (let i = 0; i < s.length; i++) {
      const c = s[i];
      if (OPENERS.has(c)) {
        stack.push(c);
        built.push({ index: i, char: c, stack: [...stack], action: 'push' });
      } else if (PAIRS[c]) {
        if (stack.length === 0 || stack[stack.length - 1] !== PAIRS[c]) {
          built.push({ index: i, char: c, stack: [...stack], action: 'mismatch' });
          failed = true;
          break;
        }
        stack.pop();
        built.push({ index: i, char: c, stack: [...stack], action: 'match' });
      } else {
        built.push({ index: i, char: c, stack: [...stack], action: 'skip' });
      }
    }
    const valid = !failed && stack.length === 0;
    built.push({ done: true, valid, stack: [...stack] });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const note = !cur ? 'Enter a string of brackets, then Check.' :
    cur.done ? (cur.valid ? 'Every bracket was closed in the right order, and the stack ended empty — valid!' : cur.stack.length > 0 ? 'String ended with unclosed brackets still on the stack — invalid.' : 'A closing bracket didn\'t match the top of the stack — invalid.') :
    cur.action === 'push' ? `'${cur.char}' is an opening bracket — push it onto the stack.` :
    cur.action === 'match' ? `'${cur.char}' correctly closes the top of the stack — pop it off.` :
    cur.action === 'mismatch' ? `'${cur.char}' does NOT match the top of the stack — stop immediately, invalid.` :
    `'${cur.char}' isn't a bracket — skip it.`;

  const code = {
    lines: [
      'Deque<Character> stack = new ArrayDeque<>();',
      'for (char c : s.toCharArray()) {',
      '    if (c == \'(\' || c == \'[\' || c == \'{\') {',
      '        stack.push(c);',
      '    } else {',
      '        if (stack.isEmpty() || !matches(stack.pop(), c)) {',
      '            return false;',
      '        }',
      '    }',
      '}',
      'return stack.isEmpty();',
    ],
    active: !cur ? -1 : cur.done ? 10 : cur.action === 'push' ? 3 : cur.action === 'mismatch' ? 6 : cur.action === 'match' ? 5 : 1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingBottom: 18 }}>
          {text.trim().split('').map((c, i) => (
            <div key={i} style={{
              width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 15, fontFamily: "'Inter',sans-serif",
              background: cur && !cur.done && cur.index === i ? (cur.action === 'mismatch' ? `linear-gradient(135deg,${RED},#c0293a)` : `linear-gradient(135deg,${V1},${V2})`) : 'var(--bg3)',
              border: `1.5px solid ${cur && !cur.done && cur.index === i ? (cur.action === 'mismatch' ? RED : V1) : 'var(--border)'}`,
              color: cur && !cur.done && cur.index === i ? '#fff' : 'var(--text)',
            }}>{c}</div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 14 }}>
          <StackView label="stack" stack={cur ? cur.stack : []} highlightTop={cur && !cur.done && cur.action === 'push'} badTop={cur && !cur.done && cur.action === 'mismatch'} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={text} onChange={e => { setText(e.target.value); setSteps(null); }} style={wideInput} placeholder="{[()]}" />
            <button onClick={startCheck} style={smallBtn(V1)}>Check</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Valid Parentheses" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Next Greater Element (Monotonic Stack) ─────────────────────── */
const NextGreaterMode = () => {
  const [arrInput, setArrInput] = useState('4, 5, 2, 10, 8');
  const [arr, setArr] = useState([4, 5, 2, 10, 8]);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startFind = () => {
    const parsed = arrInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
    if (parsed.length === 0) return;
    const built = [];
    const stack = []; // indices, values decreasing bottom to top
    const result = new Array(parsed.length).fill(-1);
    for (let i = 0; i < parsed.length; i++) {
      while (stack.length > 0 && parsed[stack[stack.length - 1]] < parsed[i]) {
        const popped = stack.pop();
        result[popped] = parsed[i];
        built.push({ i, stack: [...stack], result: [...result], action: 'resolve', resolvedIndex: popped });
      }
      stack.push(i);
      built.push({ i, stack: [...stack], result: [...result], action: 'push' });
    }
    built.push({ done: true, result: [...result] });
    setArr(parsed);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const note = !cur ? 'Build an array, then Find Next Greater.' :
    cur.done ? `Any index still on the stack when the array ends has no next greater element — result: [${cur.result.join(', ')}].` :
    cur.action === 'resolve' ? `arr[${cur.i}]=${arr[cur.i]} is greater than arr[${cur.resolvedIndex}]=${arr[cur.resolvedIndex]} sitting on top of the stack — pop it and record its answer as ${arr[cur.i]}.` :
    `Push index ${cur.i} (value ${arr[cur.i]}) onto the stack — nothing smaller than it has appeared yet, so it waits there.`;

  const code = {
    lines: [
      'int[] result = new int[arr.length];',
      'Arrays.fill(result, -1);',
      'Deque<Integer> stack = new ArrayDeque<>(); // holds indices',
      'for (int i = 0; i < arr.length; i++) {',
      '    while (!stack.isEmpty() && arr[stack.peek()] < arr[i]) {',
      '        result[stack.pop()] = arr[i];',
      '    }',
      '    stack.push(i);',
      '}',
    ],
    active: !cur ? -1 : cur.done ? -1 : cur.action === 'resolve' ? 5 : 7,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 24, paddingBottom: 18 }}>
          {arr.map((v, i) => {
            const isCurrent = cur && !cur.done && cur.i === i;
            const resolved = cur && cur.result[i] !== -1;
            return (
              <div key={i} style={{ position: 'relative', textAlign: 'center' }}>
                {isCurrent && <span style={{ position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)', fontSize: 9.5, fontWeight: 800, color: V1, whiteSpace: 'nowrap' }}>i</span>}
                <div style={{
                  width: 46, height: 46, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 800, fontFamily: "'Inter',sans-serif",
                  background: isCurrent ? `linear-gradient(135deg,${V1},${V2})` : resolved ? 'var(--bg2)' : 'var(--bg3)',
                  color: isCurrent ? '#fff' : 'var(--text)',
                  border: `1.5px solid ${isCurrent ? V1 : 'var(--border)'}`,
                }}>{v}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>→ {cur ? cur.result[i] : '?'}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 14 }}>
          <StackView label="stack (indices)" stack={cur && !cur.done ? cur.stack.map(idx => arr[idx]) : []} highlightTop={cur && !cur.done && cur.action === 'push'} color={TEAL} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={arrInput} onChange={e => { setArrInput(e.target.value); setSteps(null); }} style={wideInput} />
            <button onClick={startFind} style={smallBtn(V1)}>Find Next Greater</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Monotonic Stack" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Min Stack ──────────────────────────────────────────────────── */
const MinStackMode = () => {
  const [stack, setStack] = useState([5, 2, 4]);
  const [minStack, setMinStack] = useState([5, 2, 2]);
  const [value, setValue] = useState('');
  const [lastAction, setLastAction] = useState({ action: null });

  const push = () => {
    if (value.trim() === '' || isNaN(Number(value))) return;
    const v = Number(value);
    setStack(s => [...s, v]);
    setMinStack(m => [...m, m.length === 0 ? v : Math.min(m[m.length - 1], v)]);
    setLastAction({ action: 'push', value: v });
    setValue('');
  };
  const pop = () => {
    if (stack.length === 0) return;
    setStack(s => s.slice(0, -1));
    setMinStack(m => m.slice(0, -1));
    setLastAction({ action: 'pop' });
  };

  const currentMin = minStack.length > 0 ? minStack[minStack.length - 1] : null;
  const code = {
    lines: [
      'Deque<Integer> stack = new ArrayDeque<>();',
      'Deque<Integer> minStack = new ArrayDeque<>();',
      '',
      'void push(int v) {',
      '    stack.push(v);',
      '    int newMin = minStack.isEmpty() ? v : Math.min(minStack.peek(), v);',
      '    minStack.push(newMin);',
      '}',
      'void pop() { stack.pop(); minStack.pop(); }',
      'int getMin() { return minStack.peek(); }  // O(1)!',
    ],
    active: !lastAction.action ? -1 : lastAction.action === 'push' ? 5 : 8,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', paddingBottom: 18 }}>
          <StackView label="stack" stack={stack} highlightTop={lastAction.action === 'push'} />
          <StackView label="minStack" stack={minStack} highlightTop={lastAction.action === 'push'} color={TEAL} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={value} onChange={e => setValue(e.target.value)} style={smallInput} placeholder="value" />
            <button onClick={push} style={smallBtn(V1)}>Push</button>
            <button onClick={pop} style={smallBtn(RED, stack.length === 0)} disabled={stack.length === 0}>Pop</button>
          </div>
          {currentMin !== null && <Chip color={GREEN}>getMin() = {currentMin}</Chip>}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            Every push also pushes the CURRENT minimum onto minStack (its own top, or the new value if smaller). getMin() is always just minStack's top — O(1), no scanning.
          </p>
        </div>
      </div>
      <CodePanel title="Java — Min Stack" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const ops = [];
  let stack = [];
  const opCount = 4 + Math.floor(Math.random() * 3);
  for (let i = 0; i < opCount; i++) {
    if (stack.length === 0 || Math.random() < 0.65) {
      const v = 1 + Math.floor(Math.random() * 9);
      stack = [...stack, v];
      ops.push({ type: 'push', value: v });
    } else {
      stack = stack.slice(0, -1);
      ops.push({ type: 'pop' });
    }
  }
  const correct = stack.length > 0 ? stack[stack.length - 1] : null;
  return { ops, finalStack: stack, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const check = () => {
    if (answer.trim() === '') return;
    const correct = scenario.correct !== null && Number(answer) === scenario.correct;
    setResult(correct ? 'correct' : 'wrong');
    setScore(s => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(''); setResult(null); };

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>
        Starting from an empty stack, this sequence of operations runs:
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
        {scenario.ops.map((op, i) => (
          <Chip key={i} color={op.type === 'push' ? V1 : RED}>{op.type === 'push' ? `push(${op.value})` : 'pop()'}</Chip>
        ))}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>
        {scenario.correct === null ? 'What does the stack look like now — is it empty?' : 'What value is on top of the stack now?'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <input value={answer} onChange={e => setAnswer(e.target.value)} style={smallInput} placeholder={scenario.correct === null ? '0 for empty' : 'top value'} disabled={result !== null} />
        {result === null ? <button onClick={check} style={smallBtn(V1)}>Check</button> : <button onClick={next} style={smallBtn(TEAL)}>Next Question</button>}
      </div>
      {result && (
        <div style={{
          fontSize: 13, fontWeight: 700, padding: '10px 14px', borderRadius: 9, marginBottom: 10,
          background: result === 'correct' ? 'rgba(46,204,113,0.12)' : 'rgba(240,65,85,0.1)',
          color: result === 'correct' ? '#1e9e57' : RED,
        }}>
          {result === 'correct' ? 'Correct!' : `Not quite — the top is ${scenario.correct === null ? 'nothing, the stack is empty' : scenario.correct}.`}
        </div>
      )}
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</div>
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const StackPlayground = () => {
  const [mode, setMode] = useState('pushpop');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'pushpop' && <PushPopMode />}
      {mode === 'parens' && <ParensMode />}
      {mode === 'nextgreater' && <NextGreaterMode />}
      {mode === 'minstack' && <MinStackMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default StackPlayground;
